import { useEffect } from 'react'
import { useFloatingActionIsland } from '@/contexts/FloatingActionIslandContext'

interface FloatingActionIslandContentProps {
  show: boolean
  children: React.ReactNode
  priority?: number
  componentId: string
}

/**
 * Component that registers content to be displayed in the FloatingActionIsland
 * when show=true. Provides a clean, declarative API for floating UI elements.
 *
 * This component handles the registration/cleanup lifecycle automatically and
 * supports priority-based ordering when multiple components want to show content.
 *
 * @param show - Whether to show the content in the floating island
 * @param children - The React content to display in the island
 * @param priority - Higher priority content takes precedence (default: 0)
 * @param componentId - Unique identifier for this content registration
 *
 * @example
 * ```tsx
 * <FloatingActionIslandContent
 *   show={hasUnsavedChanges}
 *   componentId="form-save"
 *   priority={20}
 * >
 *   <div>
 *     <span>{changedFieldsCount} unsaved changes</span>
 *     <Button onClick={onSave}>Save</Button>
 *   </div>
 * </FloatingActionIslandContent>
 * ```
 */
export function FloatingActionIslandContent({
  show,
  children,
  priority = 0,
  componentId,
}: FloatingActionIslandContentProps) {
  const { registerActions, clearActions } = useFloatingActionIsland()

  useEffect(() => {
    if (show) {
      registerActions(
        [
          {
            id: componentId,
            content: children,
            priority,
          },
        ],
        componentId,
      )
    } else {
      clearActions(componentId)
    }

    return () => {
      clearActions(componentId)
    }
  }, [show, children, priority, componentId, registerActions, clearActions])

  return null
}
